/**
 * Advanced tab — diagnostic inspectors, recovery actions, and one-off
 * migrations. Grouped by blast radius: read-only inspectors first, recovery
 * actions second, and platform-mutating migrations visually quarantined in a
 * danger group at the bottom.
 */

import styles from '../../routes/admin/pools/PoolDetail.module.css';

export interface AdvancedTabProps {
  poolId: string;
  debugLoading: boolean;
  debugData: unknown;
  onFetchDebug: (opts?: { includeDownloadUrls?: boolean; bypassCache?: boolean }) => void;
  desiredLoading: boolean;
  desiredData: unknown;
  onFetchDesiredState: () => void;
  boardDepthLoading: boolean;
  boardDepthData: unknown;
  onFetchBoardDepth: () => void;
  boardHistoryLoading: boolean;
  boardHistoryData: unknown;
  onFetchBoardHistory: () => void;
  boardMuxDebugLoading: boolean;
  boardMuxDebugData: unknown;
  onFetchBoardMuxDebug: () => void;
  rematerializeLoading: boolean;
  rematerializeData: unknown;
  onRematerialize: () => void;
  /** Pool is deactivated: recovery mutations disable, inspectors stay live. */
  dormant?: boolean;
}

export function AdvancedTab(props: AdvancedTabProps) {
  const {
    poolId,
    debugLoading, debugData, onFetchDebug,
    desiredLoading, desiredData, onFetchDesiredState,
    boardDepthLoading, boardDepthData, onFetchBoardDepth,
    boardHistoryLoading, boardHistoryData, onFetchBoardHistory,
    boardMuxDebugLoading, boardMuxDebugData, onFetchBoardMuxDebug,
    rematerializeLoading, rematerializeData, onRematerialize,
    dormant = false,
  } = props;

  return (
    <>
      {/* Read-only inspectors */}
      <div className={styles.debugGroup}>
        <h3 className={styles.debugGroupTitle}>Inspectors (read-only)</h3>
        <p className={styles.debugGroupHelp}>
          Fetch internal state snapshots. Safe to run at any time; nothing is mutated.
        </p>
        <div className={styles.debugButtonRow}>
          <button
            className={styles.actionButton}
            onClick={() => onFetchDebug()}
            disabled={debugLoading}
          >
            {debugLoading ? 'Fetching...' : 'PoolDO State'}
          </button>
          <button
            className={styles.actionButton}
            onClick={() => onFetchDebug({ includeDownloadUrls: true })}
            disabled={debugLoading}
            title="PoolDO state + a per-backend request-download-url preview. Uses the 23-min SQLite cache — subsequent fetches for the same (cloud,region,app) return the cached result."
          >
            {debugLoading ? 'Fetching...' : 'PoolDO State + URL Preview'}
          </button>
          <button
            className={styles.actionButton}
            onClick={() => onFetchDebug({ includeDownloadUrls: true, bypassCache: true })}
            disabled={debugLoading}
            title="Same as URL Preview but bypasses the 23-min download-url cache — forces fresh cache HEADs. Use when diagnosing stale R2 entries after hydration lands."
          >
            {debugLoading ? 'Fetching...' : 'URL Preview (fresh)'}
          </button>
          <button
            className={styles.actionButton}
            onClick={onFetchDesiredState}
            disabled={desiredLoading}
          >
            {desiredLoading ? 'Fetching...' : 'Desired State'}
          </button>
          <button
            className={styles.actionButton}
            onClick={onFetchBoardDepth}
            disabled={boardDepthLoading}
            title="Per-(app,version) JobBoardDO depth (the new pull-based marketplace). Open = jobs sitting on the board waiting for a worker pull; holding = worker pulls currently parked. Use to verify the dark-launch gate is routing submits to the new board for gated apps and that workers are pulling."
          >
            {boardDepthLoading ? 'Fetching...' : 'Board Depth'}
          </button>
          <button
            className={styles.actionButton}
            onClick={onFetchBoardHistory}
            disabled={boardHistoryLoading}
            title="Per-(app,version) AppVersionHistoryDO summary (last 5 minutes by default). Aggregated counters JobBoardDO ships every 30s — posted, claimed, timeouts, expired plus high-water peaks. Use to see throughput on gated apps without poking the JobBoardDO directly."
          >
            {boardHistoryLoading ? 'Fetching...' : 'Board History'}
          </button>
          <button
            className={styles.actionButton}
            onClick={onFetchBoardMuxDebug}
            disabled={boardMuxDebugLoading}
            title="JobBoardMux internal state for this pool: configured markets (set by PoolDO via setMarkets), active pull loops, current hold count, geographic anchor. Use to verify multiplex apps are reaching the mux. Empty `markets` while you have multiplex apps means PoolDO's setMarkets didn't land."
          >
            {boardMuxDebugLoading ? 'Fetching...' : 'Board Mux'}
          </button>
        </div>

        {debugData !== null && (
          <>
            <h3 className={styles.jsonDumpTitle}>PoolDO Internal State</h3>
            <pre className={styles.jsonDump}>{JSON.stringify(debugData, null, 2)}</pre>
          </>
        )}
        {desiredData !== null && (
          <>
            <h3 className={styles.jsonDumpTitle}>Desired State (OrgWsDO Config)</h3>
            <pre className={styles.jsonDump}>{JSON.stringify(desiredData, null, 2)}</pre>
          </>
        )}
        {boardDepthData !== null && <BoardDepthView data={boardDepthData} />}
        {boardHistoryData !== null && <BoardHistoryView data={boardHistoryData} />}
        {boardMuxDebugData !== null && <BoardMuxView data={boardMuxDebugData} />}
      </div>

      {/* Recovery */}
      <div className={styles.debugGroup}>
        <h3 className={styles.debugGroupTitle}>Recovery</h3>
        <p className={styles.debugGroupHelp}>
          Re-run materializePool (rewrites pool_materialized_entries) and force-push the fresh
          config to PoolDO, bypassing the durable push queue. Use to recover pools whose
          materialized rows are stale or missing (e.g. coverage pills empty, launch gated as
          "no_pool"). For wedged orcs with a healthy config, prefer Refresh Desired State on
          the Machines tab — it's cheaper.
        </p>
        <div className={styles.debugButtonRow}>
          <button
            className={styles.cautionButton}
            onClick={onRematerialize}
            disabled={rematerializeLoading || dormant}
          >
            {rematerializeLoading ? 'Re-materializing…' : 'Re-materialize + Force Push'}
          </button>
        </div>
        {rematerializeData !== null && <RematerializeView data={rematerializeData} />}
      </div>

    </>
  );
}

// ---------------------------------------------------------------------------
// Structured result views
// ---------------------------------------------------------------------------

function BoardDepthView({ data }: { data: unknown }) {
  interface BoardDepth { open: number; holding: number; directs: number; muxes: number; }
  const d = data as {
    depth?: Record<string, BoardDepth>;
    fetchedAt?: string;
    errors?: number;
    errorDetails?: Record<string, { status: number | null; reason?: string }>;
    note?: string;
    error?: string;
  } | null;
  const rows = d?.depth ? Object.entries(d.depth) : [];
  const hasErrors = (d?.errors ?? 0) > 0;
  return (
    <>
      <h3 className={styles.jsonDumpTitle}>JobBoard Depth (new pull-based marketplace)</h3>
      {d?.note && (
        <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}>
          <strong>Note:</strong> {d.note}
        </div>
      )}
      {d && !d.error && (
        <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-xs)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
            <span><strong>Boards responding:</strong> {rows.length}</span>
            {hasErrors && (
              <span style={{ color: 'var(--color-error)', fontWeight: 'var(--weight-bold)' }}>
                Errors: {d.errors}
              </span>
            )}
            <span><strong>Fetched:</strong> {d.fetchedAt}</span>
          </div>
          {rows.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-2)' }}>
              <thead>
                <tr style={{ borderBottom: 'var(--border-width) solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>app:version</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Open jobs on the board (posted, not yet claimed)">Open</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Worker pulls currently parked (long-poll holds)">Holding</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Direct-worker pulls">Direct</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Mux pulls">Mux</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([key, b]) => {
                  const stuck = b.open > 0 && b.holding === 0;
                  return (
                    <tr key={key} style={{ borderBottom: 'var(--border-width) solid var(--border)' }}>
                      <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono)' }}>
                        {key}
                        {stuck && (
                          <span style={{ color: 'var(--color-error)', fontWeight: 'var(--weight-bold)', marginLeft: 'var(--space-2)' }}>
                            open jobs, no pulls held
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', padding: '4px 8px', color: b.open > 0 ? 'var(--color-warning)' : undefined }}>{b.open}</td>
                      <td style={{ textAlign: 'right', padding: '4px 8px', color: b.holding === 0 ? 'var(--color-error)' : undefined }}>{b.holding}</td>
                      <td style={{ textAlign: 'right', padding: '4px 8px' }}>{b.directs}</td>
                      <td style={{ textAlign: 'right', padding: '4px 8px' }}>{b.muxes}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
      <pre
        className={styles.jsonDump}
        style={{
          borderColor: hasErrors ? 'var(--color-error)' : undefined,
          color: d?.error ? 'var(--color-error)' : undefined,
        }}
      >
        {JSON.stringify(data, null, 2)}
      </pre>
    </>
  );
}

function BoardHistoryView({ data }: { data: unknown }) {
  interface History {
    since: number; until: number; windows: number;
    posted: number; claimed: number; timeouts: number; expired: number;
    peak_open: number; peak_holding: number; unclaimed_in_window: number;
  }
  const d = data as {
    history?: Record<string, History>;
    fetchedAt?: string;
    errors?: number;
    errorDetails?: Record<string, { status: number | null; reason?: string }>;
    note?: string;
    error?: string;
  } | null;
  const rows = d?.history ? Object.entries(d.history) : [];
  const hasErrors = (d?.errors ?? 0) > 0;
  return (
    <>
      <h3 className={styles.jsonDumpTitle}>JobBoard History (last 5 min)</h3>
      {d?.note && (
        <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}>
          <strong>Note:</strong> {d.note}
        </div>
      )}
      {d && !d.error && rows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-2)' }}>
          <thead>
            <tr style={{ borderBottom: 'var(--border-width) solid var(--border)' }}>
              <th style={{ textAlign: 'left',  padding: '4px 8px' }}>app:version</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Number of 30s flush windows aggregated">Wins</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Jobs posted to the board in window">Posted</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Claims completed in window">Claimed</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Pull-side timeouts (worker waited the full long-poll without a match)">T/O</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Jobs that expired before being claimed (TTL hit)">Exp</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }} title="High-water mark of open jobs in window">PkOpen</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }} title="High-water mark of held pulls in window">PkHold</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }} title="posted - claimed: demand the system didn't absorb in window">Δ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([key, h]) => {
              const stuck = h.unclaimed_in_window > 0;
              return (
                <tr key={key} style={{ borderBottom: 'var(--border-width) solid var(--border)' }}>
                  <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono)' }}>
                    {key}
                    {stuck && (
                      <span style={{ color: 'var(--color-warning)', marginLeft: 'var(--space-2)' }}>
                        Δ unclaimed
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{h.windows}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{h.posted}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{h.claimed}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{h.timeouts}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px', color: h.expired > 0 ? 'var(--color-warning)' : undefined }}>{h.expired}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{h.peak_open}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{h.peak_holding}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px', color: stuck ? 'var(--color-warning)' : undefined }}>{h.unclaimed_in_window}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <pre
        className={styles.jsonDump}
        style={{
          borderColor: hasErrors ? 'var(--color-error)' : undefined,
          color: d?.error ? 'var(--color-error)' : undefined,
        }}
      >
        {JSON.stringify(data, null, 2)}
      </pre>
    </>
  );
}

function BoardMuxView({ data }: { data: unknown }) {
  const d = data as {
    doName?: string;
    markets?: string[];
    activeLoops?: string[];
    center?: { lat: number; lng: number };
    holding?: number;
    error?: string;
  } | null;
  const noMarkets = !d?.error && (d?.markets?.length ?? 0) === 0;
  return (
    <>
      <h3 className={styles.jsonDumpTitle}>JobBoard Mux (new pull-based mux)</h3>
      {d && !d.error && (
        <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-xs)', display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <span><strong>DO:</strong> {d.doName ?? '(unnamed)'}</span>
          <span><strong>Markets:</strong> {d.markets?.length ?? 0}</span>
          <span><strong>Active loops:</strong> {d.activeLoops?.length ?? 0}</span>
          <span><strong>Holding:</strong> {d.holding ?? 0}</span>
          <span><strong>Center:</strong> {d.center ? `${d.center.lat.toFixed(2)}, ${d.center.lng.toFixed(2)}` : '(none)'}</span>
          {noMarkets && (
            <span style={{ color: 'var(--color-warning)' }}>
              <strong>No markets configured</strong>
              <span style={{ opacity: 0.8 }}>
                {' '}— either no app in this pool is multiplex=true, or PoolDO's setMarkets hasn't reached this mux yet
              </span>
            </span>
          )}
        </div>
      )}
      <pre
        className={styles.jsonDump}
        style={{
          borderColor: noMarkets ? 'var(--color-warning)' : undefined,
          color: d?.error ? 'var(--color-error)' : undefined,
        }}
      >
        {JSON.stringify(data, null, 2)}
      </pre>
    </>
  );
}

function RematerializeView({ data }: { data: unknown }) {
  const d = data as {
    ok?: boolean;
    materializedCount?: number;
    materializedRevision?: number;
    pushed?: boolean;
    pushError?: string;
    error?: string;
  } | null;
  const ok = d?.ok === true;
  return (
    <>
      <h3 className={styles.jsonDumpTitle}>Re-materialize + Force Push</h3>
      {d && !d.error && (
        <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-xs)', display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <span><strong>Materialized:</strong> {d.materializedCount ?? 0} entries</span>
          <span><strong>Revision:</strong> {d.materializedRevision ?? 0}</span>
          <span style={{ color: ok ? 'var(--color-success)' : 'var(--color-error)' }}>
            <strong>{ok ? '✓ Pushed to PoolDO' : '✗ Push failed'}</strong>
          </span>
        </div>
      )}
      <pre
        className={styles.jsonDump}
        style={{
          maxHeight: 300,
          borderColor: ok ? undefined : 'var(--color-error)',
          color: d?.error ? 'var(--color-error)' : undefined,
        }}
      >
        {JSON.stringify(data, null, 2)}
      </pre>
    </>
  );
}
